import React, { useEffect, useState } from "react";
import InquiryStatusBadge from "./InquiryStatusBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatRupeeAmount } from "../utils/currency";
import { getFixedDropdownPosition } from "../../../../components/common/dropdownPositionUtils";

interface InquiryRow {
  id: number;
  client_name: string;
  contact_details: string;
  client_segment: string;
  current_stage: string;
  architect_name: string;
  probability: string;
  current_status: string | null;
  inquiry_assigned_to: string;
  dp_details: string | null;
  quotation: string | null;
  latest_activity: string | null;
  scheduled_activity: string | null;
}

interface Filters {
  client_name: string;
  contact: string;
  client_segment: number | null;
  stage: string;
  status: string;
  quotation: string;
  assigned_to: string;
  probability: string;
  rfq: string;
  architect: string;
  latest_activity: string;
  scheduled_activity: string;
  activity: string;
  channel: string;
}

type ListSummary = {
  totalRfqValue: number;
  totalQuotationValue: number;
};

interface Props {
  data: InquiryRow[];
  isInitialLoad?: boolean;
  isFetching?: boolean;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  toggleOrdering: (field: string) => void;
  getSortIcon: (field: string) => any;
  clientSegmentOptions: { value: number; label: string }[];
  stageOptions?: { value: string; label: string }[];
  assignedToOptions?: { value: string; label: string }[];
  statusOptions?: { value: string; label: string }[];
  vertical: string;
  listSummary?: ListSummary;
  canDeleteEnquiry?: boolean;
  onDelete?: (id: number) => void;
}

const InquiryTable: React.FC<Props> = ({
  data,
  isInitialLoad = false,
  isFetching = false,
  filters,
  setFilters,
  toggleOrdering,
  getSortIcon,
  clientSegmentOptions,
  stageOptions = [],
  assignedToOptions = [],
  statusOptions = [],
  vertical,
  listSummary,
  canDeleteEnquiry = false,
  onDelete,
}) => {
  const formatAmountTotal = (value: number) => `₹${formatCurrency(value)}`;
  const navigate = useNavigate();
  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const close = () => setOpenActionId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  const selectedRow = data.find(row => row.id === openActionId);

  const isFinalStage =
    selectedRow?.current_stage?.toLowerCase() === "won" ||
    selectedRow?.current_stage?.toLowerCase() === "lost";
  const formatDate = (date?: string | null) => {
    if (!date) return "-";

    // handle already formatted date (dd-mm-yyyy)
    if (date.includes("-") && date.split("-")[0].length === 2) {
      return date;
    }

    const d = new Date(date);

    if (isNaN(d.getTime())) return "-";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const handleScroll = () => setOpenActionId(null);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <div className="inquiry-table-wrapper table-responsive position-relative">
      {isFetching && (
        <div className="table-loading-overlay">
          <span className="spinner-border spinner-border-sm" role="status" />
        </div>
      )}
      <table className="table inquiry-table align-middle">
        <thead>
          <tr>
            <th style={{ textAlign: "center" }}>Action</th>

            <th onClick={() => toggleOrdering("client_name")} className="sortable">
              Client Name <FontAwesomeIcon icon={getSortIcon("client_name")} />
            </th>

            <th>Contact Details</th>
            <th>Client Segment</th>
            <th>Current Stage</th>
            <th>Architect</th>
            <th>RFQ</th>
            <th>Quotation</th>

            <th onClick={() => toggleOrdering("probability")} className="sortable">
              Probability <FontAwesomeIcon icon={getSortIcon("probability")} />
            </th>

            <th>Status</th>
            <th>Assigned To</th>
            <th>Scheduled Activity</th>
            <th>Latest Activity</th>
          </tr>

          {/* ================= FILTER ROW ================= */}
          <tr className="inquiry-filter-row">
            <th></th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.client_name}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    client_name: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.contact}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    contact: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <select
                className="form-select form-select-sm"
                value={filters.client_segment ?? ""}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    client_segment: e.target.value
                      ? Number(e.target.value)
                      : null,
                  }))
                }
              >
                <option value="">All</option>
                {clientSegmentOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>



            </th>

            <th>
              <select
                className="form-select form-select-sm"
                value={filters.stage}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    stage: e.target.value,
                  }))
                }
              >
                <option value="">All</option>
                {stageOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.architect}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    architect: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.rfq}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    rfq: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.quotation}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    quotation: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <input
                className="form-control form-control-sm"
                value={filters.probability}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    probability: e.target.value,
                  }))
                }
                placeholder="Search"
              />

            </th>

            <th>
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
              >
                <option value="">All</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </th>

            <th>
              <select
                className="form-select form-select-sm"
                value={filters.assigned_to}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    assigned_to: e.target.value,
                  }))
                }
              >
                <option value="">All</option>
                {assignedToOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </th>


            <th>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.scheduled_activity}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    scheduled_activity: e.target.value,
                  }))
                }
              />

            </th>
            <th>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.latest_activity}
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    latest_activity: e.target.value,
                  }))
                }
              />
            </th>
          </tr>
        </thead>

        <tbody>
          {isInitialLoad ? (
            <tr className="inquiry-table-loading-row">
              <td colSpan={12} className="text-center py-4">
                <span className="spinner-border spinner-border-sm" role="status" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={12} className="text-center py-4">
                No inquiries found
              </td>
            </tr>
          ) : (
            data.map((row) => {
              const isFinalStage =
                row.current_stage?.toLowerCase() === "won" ||
                row.current_stage?.toLowerCase() === "lost";

              return (
                <tr key={row.id}>
                  <td style={{ textAlign: "center", position: "relative" }}>
                    <button
                      className="inquiry-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();

                        const rect = e.currentTarget.getBoundingClientRect();
                        const itemCount = canDeleteEnquiry && onDelete ? 3 : 2;

                        setDropdownPos(
                          getFixedDropdownPosition(rect, { itemCount })
                        );

                        setOpenActionId(openActionId === row.id ? null : row.id);
                      }}

                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>



                  </td>

                  <td>{row.client_name || "-"}</td>
                  <td>{row.contact_details || "-"}</td>
                  <td>{row.client_segment || "-"}</td>
                  <td>{row.current_stage || "-"}</td>
                  <td>{row.architect_name || "-"}</td>
                  <td style={{ textAlign: "right" }}>{formatRupeeAmount(row.dp_details)}</td>
                  <td style={{ textAlign: "right" }}>{formatRupeeAmount(row.quotation)}</td>
                  <td>{row.probability || "-"}</td>

                  <td>
                    {row.current_status ? (
                      <InquiryStatusBadge status={row.current_status} />
                    ) : (
                      "-"
                    )}
                  </td>

                  <td>{row.inquiry_assigned_to || "-"}</td>

                  <td style={{ textAlign: "center" }}>
                    {formatDate(row.scheduled_activity)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {formatDate(row.latest_activity)}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>

        {data.length > 0 && listSummary && (
          <tfoot>
            <tr className="inquiry-total-row">
              <td colSpan={6} className="text-end fw-semibold">
                Total
              </td>
              <td className="text-end fw-semibold">
                {formatAmountTotal(listSummary.totalRfqValue)}
              </td>
              <td className="text-end fw-semibold">
                {formatAmountTotal(listSummary.totalQuotationValue)}
              </td>
              <td colSpan={5} />
            </tr>
          </tfoot>
        )}
      </table>
      {openActionId && (
        <div
          className="inquiry-action-dropdown fixed"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              navigate(`/${vertical}/inquiries/${openActionId}/view`);
              setOpenActionId(null);
            }}
          >
            View
          </button>

          <button
            onClick={() => {
              navigate(`/${vertical}/inquiries/${openActionId}/edit`);
              setOpenActionId(null);
            }}
          >
            Edit
          </button>

          {canDeleteEnquiry && onDelete && (
            <button
              className="delete"
              onClick={() => {
                onDelete(openActionId);
                setOpenActionId(null);
              }}
            >
              Delete
            </button>
          )}

        </div>
      )}

    </div>
  );
};

export default InquiryTable;
